class OmnivaPicker extends HTMLElement {
  connectedCallback() {
    this._data = null;
    this._activeCountry = 'EE';
    this._selected = this.dataset.selected || '';
    this._dataUrl = this.dataset.url;
    this._input = this.querySelector('input[name="attributes[Omniva parcel machine]"]');
    this._render();
    this._load();
  }

  _render() {
    const tabs = this._buildTabs();
    const search = this._buildSearch();

    this._list = document.createElement('div');
    this._list.className = 'omniva-picker__list';
    this._list.setAttribute('role', 'listbox');
    this._list.appendChild(this._makeStatusDiv('Loading…'));

    this._summary = document.createElement('div');
    this._summary.className = 'omniva-picker__selected';
    this._updateSummary();

    this.prepend(tabs, search, this._list, this._summary);
  }

  _buildTabs() {
    const tabs = document.createElement('div');
    tabs.className = 'omniva-picker__tabs';
    tabs.setAttribute('role', 'tablist');

    for (const [code, label] of [['EE', '🇪🇪 Estonia'], ['LV', '🇱🇻 Latvia'], ['LT', '🇱🇹 Lithuania']]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'omniva-picker__tab';
      btn.dataset.country = code;
      btn.textContent = label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', code === this._activeCountry ? 'true' : 'false');
      btn.addEventListener('click', () => this._switchCountry(code));
      tabs.appendChild(btn);
    }

    return tabs;
  }

  _buildSearch() {
    const wrapper = document.createElement('div');
    wrapper.className = 'omniva-picker__search';

    const input = document.createElement('input');
    // type="text" avoids theme's sidebar listener on input[type="search"] (section-sidebar.js:89)
    input.type = 'text';
    input.className = 'omniva-picker__search-input';
    input.placeholder = 'Search parcel machines…';
    input.setAttribute('autocomplete', 'off');
    input.addEventListener('input', (e) => this._onSearch(e.target.value));

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'omniva-picker__search-icon');
    icon.setAttribute('width', '14');
    icon.setAttribute('height', '14');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '2');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '11');
    circle.setAttribute('cy', '11');
    circle.setAttribute('r', '8');
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '21');
    line.setAttribute('y1', '21');
    line.setAttribute('x2', '16.65');
    line.setAttribute('y2', '16.65');
    icon.appendChild(circle);
    icon.appendChild(line);

    wrapper.appendChild(input);
    wrapper.appendChild(icon);
    return wrapper;
  }

  _makeStatusDiv(text, className = 'omniva-picker__loading') {
    const div = document.createElement('div');
    div.className = className;
    div.textContent = text;
    return div;
  }

  async _load() {
    try {
      const res = await fetch(this._dataUrl);
      if (!res.ok) throw new Error(res.status);
      this._data = await res.json();

      if (this._selected) {
        for (const country of ['EE', 'LV', 'LT']) {
          const found = Object.values(this._data[country] || {}).flat().includes(this._selected);
          if (found) { this._activeCountry = country; break; }
        }
        this._updateTabUI();
      }

      this._renderList('');
    } catch {
      this._list.replaceChildren(
        this._makeStatusDiv('Failed to load locations. Please refresh.', 'omniva-picker__empty')
      );
    }
  }

  _switchCountry(code) {
    this._activeCountry = code;
    this._updateTabUI();
    const searchInput = this.querySelector('.omniva-picker__search-input');
    searchInput.value = '';
    this._renderList('');
  }

  _updateTabUI() {
    this.querySelectorAll('.omniva-picker__tab').forEach(btn => {
      btn.setAttribute('aria-selected', btn.dataset.country === this._activeCountry ? 'true' : 'false');
    });
  }

  _onSearch(query) {
    this._renderList(query.trim().toLowerCase());
  }

  _renderList(query) {
    if (!this._data) return;
    const counties = this._data[this._activeCountry] || {};
    const fragment = document.createDocumentFragment();
    let totalVisible = 0;

    for (const [county, machines] of Object.entries(counties)) {
      const filtered = query
        ? machines.filter(m => m.toLowerCase().includes(query))
        : machines;

      if (filtered.length === 0) continue;
      totalVisible += filtered.length;

      const header = document.createElement('div');
      header.className = 'omniva-picker__county';
      header.setAttribute('aria-hidden', 'true');
      header.textContent = county;
      fragment.appendChild(header);

      for (const name of filtered) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'omniva-picker__machine';
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', name === this._selected ? 'true' : 'false');
        btn.textContent = name;
        btn.addEventListener('click', () => this._select(name));
        fragment.appendChild(btn);
      }
    }

    this._list.replaceChildren();
    if (totalVisible === 0) {
      this._list.appendChild(
        this._makeStatusDiv('No matching parcel machines.', 'omniva-picker__empty')
      );
    } else {
      this._list.appendChild(fragment);
      const selected = this._list.querySelector('[aria-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }
  }

  _select(name) {
    this._selected = name;
    if (this._input) this._input.value = name;
    this._updateSummary();
    this._list.querySelectorAll('.omniva-picker__machine').forEach(btn => {
      btn.setAttribute('aria-selected', btn.textContent === name ? 'true' : 'false');
    });
    this.dispatchEvent(new CustomEvent('omniva:selected', { detail: { name }, bubbles: true }));
  }

  _updateSummary() {
    if (this._selected) {
      this._summary.textContent = `Selected: ${this._selected}`;
      this._summary.classList.add('is-visible');
    } else {
      this._summary.classList.remove('is-visible');
    }
  }
}

customElements.define('omniva-picker', OmnivaPicker);
